import { Lightning, Router } from '@lightningjs/sdk'

import { getColor, grey50, grey500, grey920, white } from '../../constants/colors'
import { UniNeueBold } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { Images } from '../../constants/paths'
import {
  ADD_FAVORITES_ENTITY_SEARCH,
  BASE_ROUTES,
  NAV_HUB,
  VERTICAL_HOME,
} from '../../constants/routes'
import { fetchData } from '../../services/flosports/floExperience'
import { createSpeechTextForCards } from '../../utils/cardUtils'
import { getAsset, getIcon } from '../../utils/image'
import { captureClick, getCardPosition, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import TvBadge from '../TvBadge/TvBadge'
import { INNER_MARGIN, INNER_PADDING, TvBadges } from './templates'
import TvBaseCard from './TvBaseCard'

const SPORT_TITLE_FONT_SIZE = 40
const SPORT_ICON_HEIGHT = 80
const SPORT_ICON_WIDTH = 80

export enum EXPERIENCES {
  HOME = 'home',
  NAV_HUB = 'nav-hub',
  FAVORITE_HUB = 'favorite-hub',
  SEARCH = 'search',
  ADD_FAVORITES = 'add-favorites',
}

export default class TvNavigationCard extends TvBaseCard {
  static _template() {
    return {
      ...super._template(),

      Container: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        clipping: true,
        color: getColor(grey920),
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
        },
        flex: {
          direction: 'column',
          justifyContent: 'space-between',
        },

        Gradient: {
          flexItem: false,
          w: (w) => w,
          h: (h) => h,

          children: [
            {
              x: 0,
              y: 0,
              visible: true,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.TV_NAVIGATION_CARD_BLUR),
                388,
                260,
              ),
            },
            {
              x: -53,
              y: -66,
              visible: false,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.FOCUS_GRADIENT_BACK),
                362,
                304,
              ),
            },
            {
              x: -26,
              y: -22,
              visible: false,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.FOCUS_GRADIENT_MIDDLE),
                261,
                217,
              ),
            },
            {
              x: -26,
              y: -22,
              visible: false,
              texture: Lightning.Tools.getSvgTexture(
                getAsset(Images.FOCUS_GRADIENT_FRONT),
                154,
                131,
              ),
            },
          ],
        },

        TopRow: {
          flex: {
            direction: 'row',
            justifyContent: 'space-between',
          },
          x: INNER_PADDING,
          y: INNER_PADDING,
          h: SPORT_ICON_HEIGHT,
          w: (w) => w - INNER_PADDING * 2,

          Icon: {
            x: 8,
            y: 8,
            w: SPORT_ICON_WIDTH - 8,
            h: SPORT_ICON_HEIGHT - 8,
            color: getColor(grey500),
          },
          TopTrailingBadges: {
            type: TvBadges,
            flexItem: {
              marginRight: INNER_MARGIN,
              marginTop: INNER_MARGIN,
            },
          },
        },
        TitleContainer: {
          flex: { direction: 'column', justifyContent: 'flex-end' },
          x: INNER_PADDING + 8,
          y: -(INNER_PADDING + 8),
          h: 96,
          w: (w) => w - (INNER_MARGIN * 2 - 8) * 2,
          Title: {
            text: {
              textColor: getColor(grey50),
              fontFace: UniNeueBold,
              fontSize: SPORT_TITLE_FONT_SIZE,
              maxLines: 2,
              maxLinesSuffix: '...',
            },
          },
        },
      },
    }
  }

  get container() {
    return this.tag('Container')
  }

  get gradient() {
    return this.tag('Gradient')
  }

  get icon() {
    return this.tag('Icon')
  }

  get topTrailingBadges() {
    return this.tag('TopTrailingBadges') as TvBadges
  }

  get title() {
    return this.tag('Title')
  }

  get speechText(): string {
    return createSpeechTextForCards(
      this.data,
      ' . Select to browse . ',
      this.cardIndex,
      this.totalCards,
    )
  }

  _active() {
    this.renderCardData()
  }

  async _handleEnter() {
    void captureClick(
      SEGMENT_TRACK_EVENTS.ButtonClicked,
      this.data?.action?.analytics,
      getCardPosition(this),
    )

    if (this.data?.action?.api_call) {
      await this.actOnApiCall()
    } else {
      this.navigateToUrl()
    }
  }

  async actOnApiCall() {
    const [badge]: [TvBadge] = this.topTrailingBadges.childList.get(0)
    badge.showSpinner = true
    const response = await fetchData(this.data.action.api_call)
    badge.showSpinner = false
    this.fireAncestors('$onApiCall', response, this.data)
  }

  navigateToUrl() {
    const urlParamsList = this.data?.action?.url?.split('?')[1]
    const urlParams = new URLSearchParams(urlParamsList)
    const siteId = urlParams.get('site_id')
    urlParams.delete('site_id')
    const navId = urlParams.get('nav_id')
    urlParams.delete('nav_id')

    let finalUrl

    if (this.data?.action?.url.includes(EXPERIENCES.SEARCH)) {
      const searchUrl = this.data?.action?.url
      const encodedUrl = encodeURIComponent(searchUrl)
      finalUrl = `${BASE_ROUTES.SEARCH}/${encodedUrl}`
    } else if (this.data?.action?.url?.includes(EXPERIENCES.NAV_HUB)) {
      finalUrl = `${NAV_HUB.replace(':siteId', siteId).replace(
        ':navId',
        navId,
      )}?${urlParams.toString()}`
    } else if (this.data?.action?.url?.includes(EXPERIENCES.HOME)) {
      finalUrl = `${VERTICAL_HOME.replace(':siteId', siteId)}?${urlParams.toString()}`
    } else if (this.data?.action?.url.includes(EXPERIENCES.ADD_FAVORITES)) {
      const entitySearchUrl = this.data?.action?.url
      const encodedUrl = encodeURIComponent(entitySearchUrl)
      finalUrl = ADD_FAVORITES_ENTITY_SEARCH.replace(':url', encodedUrl)
    } else if (this.data?.action?.url.includes(EXPERIENCES.FAVORITE_HUB)) {
      const favoritesUrl = this.data?.action?.url
      const encodedUrl = encodeURIComponent(favoritesUrl)
      finalUrl = `${BASE_ROUTES.FAVORITE_HUB}/${encodedUrl}`
    } else {
      finalUrl = '*'
    }

    Router.navigate(finalUrl)
  }

  renderCardData() {
    this.icon.patch({
      texture: Lightning.Tools.getSvgTexture(
        getIcon(this.data?.vertical_icon?.style),
        SPORT_ICON_WIDTH,
        SPORT_ICON_HEIGHT,
      ),
    })
    this.topTrailingBadges.patch({
      ...(this.data?.trailing_top_badges?.length > 0 && {
        badges: this.data.trailing_top_badges,
      }),
    })
    this.title.patch({
      text: {
        text: this.data?.title?.text ?? '',
        wordWrapWidth: this.cardWidth - (INNER_MARGIN * 2 - 8) * 2,
      },
    })
  }

  renderGradient(isFocused?: boolean) {
    this.gradient.childList.getAt(0).patch({ visible: !isFocused })
    this.gradient.childList.getAt(1).patch({ visible: isFocused })
    this.gradient.childList.getAt(2).patch({ visible: isFocused })
    this.gradient.childList.getAt(3).patch({ visible: isFocused })
  }

  _focus() {
    super._focus()
    this.renderGradient(true)
    this.icon.patch({
      color: getColor(white),
    })
  }

  _unfocus() {
    super._unfocus()
    this.renderGradient(false)
    this.icon.patch({
      color: getColor(grey500),
    })
  }

  _handleClick() {
    this._handleEnter()
  }
}
